/* istanbul ignore file - better tested visually */

import { useCursor } from '@react-three/drei';
import { invalidate, ThreeEvent } from '@react-three/fiber';
import { EffectComposer } from '@react-three/postprocessing';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Object3D } from 'three';
import { Highlight } from '../Highlight/Highlight';
import { ModelViewer, ModelViewerProps } from '../ModelViewer/ModelViewer';
import styles from './ModelEditor.module.scss';
import {
  TransformMultipleControls,
  TransformMultipleControlsProps,
} from './TransformMultipleControls/TransformMultipleControls';

export type ModelEditorContextProps = {
  model: Object3D;
  selected?: Object3D[];
  hovered?: Object3D;
  isTransforming: boolean;
};

const ModelEditorContext = createContext<ModelEditorContextProps>({
  model: new Object3D(),
  isTransforming: false,
});

export const useModelEditor = () => useContext(ModelEditorContext);

export type ModelEditorProps = {
  model: Object3D;
  canSelect?: boolean;
  canTransform?: boolean;
  transformMode?: TransformMultipleControlsProps['mode'];
  selected?: Object3D[];
  onSelect?: (selection: Object3D[]) => void;
  viewerProps?: Omit<ModelViewerProps, 'model'>;
  interfaceElements?: {
    topLeft?: ReactNode;
    topRight?: ReactNode;
    bottomLeft?: ReactNode;
    bottomRight?: ReactNode;
  };
};

export function ModelEditor({
  model,
  canSelect = true,
  canTransform = false,
  transformMode = 'translate',
  selected,
  onSelect,
  interfaceElements = {},
  viewerProps: { modelProps, children, ...restViewerProps } = {},
}: Readonly<ModelEditorProps>) {
  /* Selection and Hover states */
  const [hovered, setHovered] = useState<Object3D>();
  const highlighted = useMemo(() => (selected?.length ? selected : hovered), [hovered, selected]);

  /* Handle model interactions */
  const [isTransforming, setIsTransforming] = useState(false);
  const handleClick = useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      e.stopPropagation();
      if (canSelect && !isTransforming && onSelect != null) {
        if (e.shiftKey && selected?.includes(e.object)) {
          onSelect(selected.filter((selected) => selected !== e.object));
        } else if (e.shiftKey) {
          onSelect(Array.from(new Set([...(selected ?? []), e.object])));
        } else {
          onSelect([e.object]);
        }
      }
      modelProps?.onClick?.(e);
    },
    [canSelect, isTransforming, modelProps, onSelect, selected]
  );
  const handlePointerMissed = useCallback(
    (e: MouseEvent) => {
      if (canSelect && !isTransforming && e.button === 0) {
        onSelect?.([]);
      }
      modelProps?.onPointerMissed?.(e);
    },
    [canSelect, isTransforming, modelProps, onSelect]
  );
  const handlePointerOver = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      if (canSelect && !isTransforming) {
        setHovered(e.object);
      }
      modelProps?.onPointerOver?.(e);
    },
    [canSelect, isTransforming, modelProps]
  );
  const handlePointerOut = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      if (canSelect && !isTransforming) {
        setHovered(undefined);
      }
      modelProps?.onPointerOut?.(e);
    },
    [canSelect, isTransforming, modelProps]
  );
  const handleTransformStart = useCallback(() => {
    setIsTransforming(true);
  }, []);
  const handleTransformEnd = useCallback(() => {
    setIsTransforming(false);
  }, []);

  /* Change cursor when an model object is hovered */
  useCursor(hovered != null);

  /* Force a re-render to show/hide hover outlines */
  useEffect(() => {
    invalidate();
  }, [hovered]);

  /* Get values for context */
  const contextValues = useMemo<ModelEditorContextProps>(
    () => ({
      model,
      isTransforming,
      hovered,
      selected,
    }),
    [hovered, isTransforming, model, selected]
  );

  return (
    <ModelEditorContext.Provider value={contextValues}>
      <div className={styles.modelEditor}>
        <div className={styles.canvas}>
          <ModelViewer
            /* Don't intersect with hidden objects by default (e.g. for mouse events) */
            onCreated={(state) => {
              state.setEvents({ filter: (intersections) => intersections.filter((i) => i.object.visible) });
            }}
            {...restViewerProps}
            model={model}
            modelProps={{
              ...modelProps,
              onClick: handleClick,
              onPointerMissed: handlePointerMissed,
              onPointerOver: handlePointerOver,
              onPointerOut: handlePointerOut,
            }}
          >
            <EffectComposer autoClear={false}>
              {children as any}
              {selected && selected.filter((selected) => selected.visible).length > 0 && canTransform && (
                <TransformMultipleControls
                  objects={selected}
                  mode={transformMode}
                  onMouseDown={handleTransformStart}
                  onMouseUp={handleTransformEnd}
                />
              )}
              {canSelect && <Highlight selection={highlighted} color={0xfd5305} selectionLayer={2} />}
            </EffectComposer>
          </ModelViewer>
        </div>
        <div className={styles.interface}>
          <div className={styles.topLeft}>{interfaceElements.topLeft}</div>
          <div className={styles.topRight}>{interfaceElements.topRight}</div>
          <div className={styles.bottomLeft}>{interfaceElements.bottomLeft}</div>
          <div className={styles.bottomRight}>{interfaceElements.bottomRight}</div>
        </div>
      </div>
    </ModelEditorContext.Provider>
  );
}
